import React, { createContext, useContext, useState, useEffect } from 'react';

const SettingsContext = createContext();

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState({
    orderCutoffTime: '16:00', // 4:00 PM default
    orderCutoffDays: 1, // Orders close 1 day before delivery
    deliveryDays: 2, // Delivery 2 days after order
    isOrderingOpen: true
  });

  // Load settings from localStorage on mount
  useEffect(() => {
    const savedSettings = localStorage.getItem('appSettings');
    if (savedSettings) {
      setSettings(JSON.parse(savedSettings));
    }
  }, []);

  // Save settings to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('appSettings', JSON.stringify(settings));
  }, [settings]);

  // Calculate next order deadline
  const getNextOrderDeadline = () => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    // Parse cut-off time
    const [hours, minutes] = settings.orderCutoffTime.split(':').map(Number);
    const cutoffTime = new Date(today);
    cutoffTime.setHours(hours, minutes, 0, 0);
    
    // If today's cutoff has passed, move to next day
    if (now > cutoffTime) {
      cutoffTime.setDate(cutoffTime.getDate() + 1);
    }
    
    return cutoffTime;
  };

  // Calculate next delivery date
  const getNextDeliveryDate = () => {
    const now = new Date();
    const currentHour = now.getHours();
    
    // If current time is after 16:00 (4 PM), delivery is next day
    // Otherwise, delivery is same day
    const deliveryDate = new Date();
    if (currentHour >= 16) {
      deliveryDate.setDate(deliveryDate.getDate() + 1);
    }
    
    return deliveryDate;
  };

  // Check if ordering is currently open
  const isOrderingCurrentlyOpen = () => {
    if (!settings.isOrderingOpen) return false;
    
    const now = new Date();
    const deadline = getNextOrderDeadline();
    return now < deadline;
  };

  // Get time remaining until deadline
  const getTimeUntilDeadline = () => {
    const now = new Date();
    const deadline = getNextOrderDeadline();
    const timeDiff = deadline.getTime() - now.getTime();
    
    if (timeDiff <= 0) {
      return { hours: 0, minutes: 0, seconds: 0, total: 0 };
    }
    
    const hours = Math.floor(timeDiff / (1000 * 60 * 60));
    const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000);
    
    return { hours, minutes, seconds, total: timeDiff };
  };

  // Update settings (admin function)
  const updateSettings = (newSettings) => {
    setSettings(prev => ({ ...prev, ...newSettings }));
  };

  const value = {
    settings,
    updateSettings,
    getNextOrderDeadline,
    getNextDeliveryDate,
    isOrderingCurrentlyOpen,
    getTimeUntilDeadline
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
}; 